import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSearchPlus } from '@fortawesome/free-solid-svg-icons';
import { NavigationContext } from '../context/NavigationContext';
import DemoWalkthrough, { DontShowLink } from '../components/DemoWalkthrough';
import { shouldResume, setStep } from '../utils/walkthroughState';
import API_URL from '../config';

const SleepParameterDetail = () => {
  const [searchParams] = useSearchParams();
  const { recordingId } = useParams();
  const { openLevel } = useContext(NavigationContext);
  const [parameter, setParameter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualValue, setManualValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [rolCalculation, setRolCalculation] = useState(null);
  const [ruleBasedEstimator, setRuleBasedEstimator] = useState(null);
  const [patternBasedEstimator, setPatternBasedEstimator] = useState(null);

  const paramName = searchParams.get('param');

  // Freeze at mount so DemoWalkthrough's clearStep() can't flip it to false on re-render.
  const [isDemo] = useState(() => shouldResume('parameter-detail', recordingId));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sleep-parameters/?name=${encodeURIComponent(paramName)}&recording_id=${recordingId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch sleep parameter');
        }
        const data = await response.json();
        console.log('API Response:', data);  // Debug log
        
        // Find the parameter matching the requested name
        const paramData = data.find(p => p.name === paramName);
        if (paramData) {
          console.log('Parameter data:', paramData);  // Debug log
          setParameter(paramData);
          setManualValue(paramData.value);
          
          // Always set estimators regardless of manual override
          const ruleBased = paramData.estimators?.find(e => e.method === 'rule-based');
          const patternBased = paramData.estimators?.find(e => e.method === 'pattern-based');
          console.log('Rule-based estimator:', ruleBased);  // Debug log
          console.log('Pattern-based estimator:', patternBased);  // Debug log
          
          setRuleBasedEstimator(ruleBased || null);
          setPatternBasedEstimator(patternBased || null);

          // For REM Onset Latency, fetch epochs to calculate the value
          if (paramName === "REM Onset Latency") {
            const epochsResponse = await fetch(`${API_URL}/api/epochs/?recording_id=${recordingId}`);
            if (!epochsResponse.ok) {
              throw new Error('Failed to fetch epochs');
            }
            const epochs = await epochsResponse.json();
            
            // Sort epochs by epoch number to ensure chronological order
            const sortedEpochs = epochs.sort((a, b) => a.epoch_number - b.epoch_number);
            
            // Find first sleep epoch (N1, N2, N3, or REM)
            const firstSleepEpoch = sortedEpochs.find(epoch => 
              ['N1', 'N2', 'N3', 'REM'].includes(epoch.sleep_stage)
            );

            // Find first REM epoch
            const firstRemEpoch = sortedEpochs.find(epoch => 
              epoch.sleep_stage === 'REM'
            );

            if (firstSleepEpoch && firstRemEpoch) {
              // Calculate REM Onset Latency in minutes
              const remOnsetLatency = (firstRemEpoch.epoch_start - firstSleepEpoch.epoch_start) / 60;
              
              setRolCalculation({
                firstSleepEpoch: {
                  epochNumber: firstSleepEpoch.epoch_number,
                  time: new Date(firstSleepEpoch.epoch_start * 1000).toLocaleTimeString(),
                  stage: firstSleepEpoch.sleep_stage,
                  id: firstSleepEpoch.id
                },
                firstRemEpoch: {
                  epochNumber: firstRemEpoch.epoch_number,
                  time: new Date(firstRemEpoch.epoch_start * 1000).toLocaleTimeString(),
                  stage: 'REM',
                  id: firstRemEpoch.id
                },
                value: remOnsetLatency.toFixed(1)
              });
            } else {
              setRolCalculation({
                error: firstSleepEpoch ? 'No REM epochs found' : 'No sleep epochs found'
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load parameter data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [paramName]);

  const handleManualUpdate = async () => {
    try {
      // First update the sleep parameter
      const paramResponse = await fetch(`${API_URL}/api/sleep-parameters/${parameter.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          value: parseFloat(manualValue),
          manual_override: true
        }),
      });

      if (!paramResponse.ok) {
        throw new Error('Failed to update parameter');
      }

      // Find and delete any existing manual estimator
      const existingManual = parameter.estimators?.find(e => e.method === 'manual');
      if (existingManual) {
        const deleteResponse = await fetch(`${API_URL}/api/sleep-parameter-estimators/${existingManual.id}/`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!deleteResponse.ok) {
          console.warn('Failed to delete existing manual estimator');
        }
      }

      // Then create a manual estimator
      const estimatorResponse = await fetch(`${API_URL}/api/sleep-parameter-estimators/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sleep_parameter: parameter.id,
          method: 'manual',
          value: parseFloat(manualValue),
          explanation: {
            "method": "Manual override by user"
          }
        }),
      });

      if (!estimatorResponse.ok) {
        console.warn('Failed to create manual estimator');
      }

      // Fetch the updated parameter with all its estimators
      const refreshResponse = await fetch(`${API_URL}/api/sleep-parameters/${parameter.id}/`);
      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh parameter data');
      }

      const updatedParameter = await refreshResponse.json();
      setParameter(updatedParameter);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating parameter:', err);
      setError(err.message);
    }
  };

  const handleRemoveManualOverride = async () => {
    try {
      // Update the sleep parameter to remove manual override
      const paramResponse = await fetch(`${API_URL}/api/sleep-parameters/${parameter.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manual_override: false,
          value: ruleBasedEstimator?.value || patternBasedEstimator?.value || 0
        }),
      });

      if (!paramResponse.ok) {
        throw new Error('Failed to update parameter');
      }

      // Fetch the updated parameter with all its estimators
      const refreshResponse = await fetch(`${API_URL}/api/sleep-parameters/${parameter.id}/`);
      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh parameter data');
      }

      const updatedParameter = await refreshResponse.json();
      setParameter(updatedParameter);
      setIsEditing(false);
    } catch (err) {
      console.error('Error removing manual override:', err);
      setError(err.message);
    }
  };

  const handleExploreEpoch = (epochData) => {
    if (paramName === "N3 Duration") {
      if (isDemo) setStep('hypnogram', recordingId);
      openLevel({
        key: 'hypnogram',
        label: 'Hypnogram',
        route: `/analysis/${recordingId}/hypnogram?from=${encodeURIComponent(paramName)}`
      });
    } else if (epochData && epochData.id) {
      openLevel({
        key: 'epoch',
        label: `Epoch ${epochData.epochNumber}`,
        route: `/analysis/${recordingId}/epoch/${epochData.id}`
      });
    } else {
      console.error('Invalid epoch data:', epochData);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600 dark:text-gray-300">Loading parameter details...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  if (!parameter) {
    return <div className="p-6 text-gray-600 dark:text-gray-300">No parameter found</div>;
  }

  const getMethodChipClass = (method) => {
    switch (method) {
      case 'rule-based':
        return 'bg-blue-500 text-white';
      case 'pattern-based':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200';
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900">
      <DemoWalkthrough
        enabled={isDemo}
        steps={isDemo ? [
          {
            target: 'body',
            title: `📊 ${paramName}`,
            content: 'This page compares two independent methods for estimating this parameter. The pattern-based (ML) estimate and the rule-based (AASM rules) estimate are shown side-by-side. Disagreements between them are flagged on the Report page.',
            placement: 'center',
            disableBeacon: true,
          },
          {
            target: '#param-pattern-section',
            title: '🤖 Pattern-based Estimate',
            content: 'The machine-learning model estimates this parameter by analysing signal patterns across the full recording. It was trained on thousands of annotated sleep studies.',
            placement: 'bottom',
            disableBeacon: true,
          },
          {
            target: '#param-rulebased-section',
            title: '📏 Rule-based Estimate',
            content: (
              <>The AASM scoring rules are applied epoch-by-epoch to classify each 30-second window. N3 Duration is the total time spent in N3 epochs. Click the magnifying glass to open the Hypnogram.<DontShowLink /></>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightClicks: true,
            hideNextButton: true,
            onNext: () => {
              handleExploreEpoch();
            },
          },
        ] : []}
      />
      <div className="space-y-6">
        {/* Rule-based and Pattern-based Section in a grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Pattern-based Section */}
          <div id="param-pattern-section" className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 transition-colors ${parameter.manual_override ? 'opacity-50' : ''}`}>
            <div className="flex flex-col">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 pb-2 border-b border-gray-100 dark:border-gray-700">
                Pattern-based Estimation
              </h2>
              {patternBasedEstimator ? (
                <div>
                  <div className="flex items-baseline mb-6">
                    <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                      {Number(patternBasedEstimator.value).toFixed(1)}
                    </span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400 font-medium">minutes</span>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-4">
                    <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Estimation Method</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {paramName === "N3 Duration" 
                        ? "This estimation is based on direct pattern analysis of the EEG signal using machine learning algorithms."
                        : "This estimation is based on pattern analysis using machine learning algorithms."}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No pattern-based estimation available</p>
              )}
            </div>
          </div>

          {/* Rule-based Section */}
          <div id="param-rulebased-section" className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 transition-colors ${parameter.manual_override ? 'opacity-50' : ''}`}>
            <div className="flex flex-col">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 pb-2 border-b border-gray-100 dark:border-gray-700">
                Rule-based Estimation
              </h2>
              {paramName === "REM Onset Latency" ? (
                rolCalculation?.error ? (
                  <div className="text-red-600 dark:text-red-400">
                    {rolCalculation.error}
                  </div>
                ) : !rolCalculation ? (
                  <div className="text-gray-600 dark:text-gray-400">
                    Loading REM Onset Latency calculation...
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline mb-6">
                      <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                        {Number(rolCalculation.value).toFixed(1)}
                      </span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400 font-medium">minutes</span>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-4">
                      <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Calculation Method</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        REM Onset Latency is calculated as the time difference between the first epoch of sleep and the first epoch of REM sleep.
                      </p>
                      
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">First Sleep Epoch:</span>
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Epoch {rolCalculation.firstSleepEpoch.epochNumber} ({rolCalculation.firstSleepEpoch.time})
                          </span>
                          <button
                            onClick={() => handleExploreEpoch(rolCalculation.firstSleepEpoch)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title="Explore epoch"
                          >
                            <FontAwesomeIcon icon={faSearchPlus} />
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">First REM Epoch:</span>
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Epoch {rolCalculation.firstRemEpoch.epochNumber} ({rolCalculation.firstRemEpoch.time})
                          </span>
                          <button
                            onClick={() => handleExploreEpoch(rolCalculation.firstRemEpoch)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title="Explore epoch"
                          >
                            <FontAwesomeIcon icon={faSearchPlus} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : paramName === "N3 Duration" ? (
                <div>
                  {console.log('Rendering N3 Duration, rule-based estimator:', ruleBasedEstimator)}
                  <div className="flex items-baseline mb-6">
                    <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                      {ruleBasedEstimator ? Number(ruleBasedEstimator.value).toFixed(1) : 'N/A'}
                    </span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400 font-medium">minutes</span>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-4">
                    <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Calculation Method</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      N3 Duration is calculated by summing the duration of all epochs classified as N3 sleep stage.
                      Each epoch is 30 seconds long.
                    </p>
                    <button
                      id="param-hypnogram-btn"
                      onClick={() => handleExploreEpoch()}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors flex items-center gap-2"
                      title="View in Hypnogram"
                    >
                      <FontAwesomeIcon icon={faSearchPlus} />
                      <span>View in Hypnogram</span>
                    </button>
                  </div>
                </div>
              ) : ruleBasedEstimator ? (
                <div>
                  <div className="flex items-baseline mb-6">
                    <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                      {Number(ruleBasedEstimator.value).toFixed(1)}
                    </span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400 font-medium">minutes</span>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-4">
                    <h3 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-200">Calculation Method</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      This estimation is based on predefined rules and clinical guidelines.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No rule-based estimation available</p>
              )}
            </div>
          </div>
        </div>

        {/* Manual Override Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 transition-colors mt-6">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => !isEditing && setIsEditing(!isEditing)}
          >
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Manual Override</h2>
              {parameter.manual_override && (
                <div className="flex items-baseline">
                  <span className="text-xl font-bold text-gray-800 dark:text-gray-100">
                    {Number(parameter.value).toFixed(1)}
                  </span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 font-medium">minutes</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              {parameter.manual_override && !isEditing && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveManualOverride();
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  Remove Override
                </button>
              )}
              {!isEditing && (
                <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                  <FontAwesomeIcon icon={faPencilAlt} />
                </button>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="number"
                    value={manualValue}
                    onChange={(e) => setManualValue(e.target.value)}
                    className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="Enter value"
                    step="0.1"
                  />
                  <span className="text-gray-600 dark:text-gray-400 font-medium">minutes</span>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={handleManualUpdate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SleepParameterDetail; 